import { Box, Typography } from "@mui/material";
import { toPng } from "html-to-image";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import DonutChart from "../../components/chart/DonutChart";
import { scope1Categories, scope2Categories, scope3Categories } from "../../pages/Organization/ScopeCategory";

const OrganizationFootprintEnvDoughnut = forwardRef(({ emissions }, parentRef) => {
  const ref = useRef(null);
  const [totalGWPs, setTotalGWPs] = useState({
    scope1: 0, scope2: 0, scope3: 0, scope4: 0, scope5: 0,
  });

  useEffect(() => {
    let scope1 = 0, scope2 = 0, scope3 = 0, scope4 = 0, scope5 = 0;
    (emissions || []).forEach((item) => {
      const gwpVal = Number(item.gwp) || 0;
      if (!item.is_avoided && !item.is_biogenic && scope1Categories.includes(item.category)) scope1 += gwpVal;
      else if (!item.is_avoided && !item.is_biogenic && scope2Categories.includes(item.category)) scope2 += gwpVal;
      else if (!item.is_avoided && !item.is_biogenic && scope3Categories.includes(item.category)) scope3 += gwpVal;
      
      if (item.is_avoided) scope4 += gwpVal;
      if (item.is_biogenic) scope5 += gwpVal;
    });
    setTotalGWPs({ scope1, scope2, scope3, scope4, scope5 });
  }, [emissions]);

  useImperativeHandle(parentRef, () => ({
    getScreenshot: async () => {
      if (ref.current) {
        return await toPng(ref.current, { cacheBust: true, backgroundColor: "#ffffff" });
      }
      return null;
    },
  }));

  const colors = ["#ef4444", "#f59e0b", "#0ea5e9", "#22c55e"];
  const totalPortfolio = totalGWPs.scope1 + totalGWPs.scope2 + totalGWPs.scope3;

  const donutDataNew = [
    { label: "Scope 1", value: totalGWPs.scope1, color: colors[0] },
    { label: "Scope 2", value: totalGWPs.scope2, color: colors[1] },
    { label: "Scope 3", value: totalGWPs.scope3, color: colors[2] },
  ];

  return (
    <Box ref={ref} sx={{ p: 4, height: "100%", borderRadius: 4, bgcolor: "#ffffff", border: "1px solid #e2e8f0" }}>
      <Typography variant="h6" sx={{ mb: 4, pb: 2, fontWeight: 500, color: "#0f172a", borderBottom: "1px solid #e2e8f0" }}>
        Scopes Summary
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
        <DonutChart
          data={donutDataNew.filter(d => Math.abs(d.value) > 0)}
          size={300}
          strokeWidth={30}
          centerContent={(
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: "#64748b", display: 'block', fontWeight: 500 }}>TOTAL FOSSIL</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: "#0f172a" }}>{totalPortfolio.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Typography>
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500 }}>kg CO₂e</Typography>
            </Box>
          )}
        />
      </Box>
      <Box sx={{ mt: 2 }}>
        {donutDataNew.map((d, i) => Math.abs(d.value) > 0 ? (
          <Box key={i} sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: d.color }} />
              <Typography variant="body2" sx={{ fontWeight: 500, color: "#475569" }}>{d.label}</Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 400, color: "#0f172a" }}>{d.value.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg ({totalPortfolio > 0 ? ((d.value / totalPortfolio) * 100).toFixed(1) : 0}%)</Typography>
          </Box>
        ) : null)}
      </Box>

      {Math.abs(totalGWPs.scope4) > 0 && (
        <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: colors[3] }} />
            <Typography variant="body2" sx={{ fontWeight: 500, color: "#475569" }}>Avoided Carbon</Typography>
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }}>{totalGWPs.scope4.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg</Typography>
        </Box>
      )}

      {Math.abs(totalGWPs.scope5) > 0 && (
        <Box sx={{ mt: Math.abs(totalGWPs.scope4) > 0 ? 0 : 2, pt: Math.abs(totalGWPs.scope4) > 0 ? 0 : 2, borderTop: Math.abs(totalGWPs.scope4) > 0 ? "none" : "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#8b5cf6" }} />
            <Typography variant="body2" sx={{ fontWeight: 500, color: "#475569" }}>Biogenic Carbon</Typography>
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a" }}>{totalGWPs.scope5.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg</Typography>
        </Box>
      )}
    </Box>
  );
});

export default OrganizationFootprintEnvDoughnut;
